/** Owns the one-time-to-recurring conversion form and atomic repository mutation. */
import type { OneTimeActivity } from '@life-rpg/shared';
import React, { useState } from 'react';

import type { OneTimeActivityRepository } from './activityRepository.js';
import { newRecurringActivityForm, parseRecurringActivityForm } from './recurringActivityForm.js';
import { RecurringActivityFields } from './RecurringActivityFields.js';

interface OneTimeActivityConversionProps {
  activity: OneTimeActivity;
  anchor: Date;
  repository: OneTimeActivityRepository;
  onCancel: () => void;
  onConverted: (id: string) => void;
  onProblem: (problem: string | null) => void;
}

export const OneTimeActivityConversion = ({
  activity,
  anchor,
  repository,
  onCancel,
  onConverted,
  onProblem,
}: OneTimeActivityConversionProps): React.JSX.Element => {
  const [recurring, setRecurring] = useState(() => newRecurringActivityForm(anchor, activity));
  const [countCompletedOccurrence, setCountCompletedOccurrence] = useState(
    activity.completedAt !== undefined,
  );
  const [completedQty, setCompletedQty] = useState('1');
  const [saving, setSaving] = useState(false);

  const convertActivity = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    onProblem(null);
    const parsed = parseRecurringActivityForm(recurring);
    if (parsed.problem !== undefined) {
      onProblem(parsed.problem);
      return;
    }
    const numericCompletedQty = Number(completedQty);
    if (
      countCompletedOccurrence &&
      (!Number.isInteger(numericCompletedQty) || numericCompletedQty <= 0)
    ) {
      onProblem('Completed quantity must be a positive whole number.');
      return;
    }

    setSaving(true);
    try {
      await repository.convert(
        activity.id,
        parsed.draft,
        countCompletedOccurrence ? numericCompletedQty : undefined,
      );
      onConverted(activity.id);
    } catch (error) {
      onProblem(error instanceof Error ? error.message : 'Could not convert activity.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="activity-form" onSubmit={(event) => void convertActivity(event)}>
      <RecurringActivityFields form={recurring} setForm={setRecurring} />
      {activity.completedAt !== undefined && (
        <>
          <label className="inline-checkbox">
            <input
              checked={countCompletedOccurrence}
              type="checkbox"
              onChange={(event) => setCountCompletedOccurrence(event.target.checked)}
            />
            Count the completed occurrence
          </label>
          {countCompletedOccurrence && (
            <label>
              Completed quantity
              <input
                required
                min="1"
                step="1"
                type="number"
                value={completedQty}
                onChange={(event) => setCompletedQty(event.target.value)}
              />
            </label>
          )}
        </>
      )}
      <div className="form-actions">
        <button disabled={saving} type="submit">
          {saving ? 'Saving…' : 'Save as recurring'}
        </button>
        <button className="secondary" disabled={saving} type="button" onClick={onCancel}>
          Cancel conversion
        </button>
      </div>
    </form>
  );
};
